import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const EFFECTIVE = "June 2026";
const CONTACT = "privacy@bracednomore.info";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="mb-2 font-display text-xl text-ink">{title}</h2>
    <div className="space-y-2 text-[15px] leading-relaxed text-ink-soft">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-[100dvh] watercolor-wash">
      <div className="mx-auto max-w-2xl px-5 py-10 safe-top safe-bottom safe-x sm:px-8">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="mb-8 inline-flex items-center gap-2 font-display text-sm text-ink-soft hover:text-gold-deep"
        >
          <ArrowLeft className="h-4 w-4" /> Back to the book
        </Link>

        <p className="tracking-editorial text-[11px] uppercase text-ink-muted">Braced No More</p>
        <h1 className="mt-1 font-display text-4xl text-ink sm:text-5xl">Privacy Policy</h1>
        <p className="mt-2 text-sm text-ink-muted">Effective {EFFECTIVE}</p>

        <div className="mt-8" data-testid="privacy-content">
          <Section title="The short version">
            <p>
              Braced No More is a quiet reading experience. We collect as little as possible. We do
              not ask for your name, we show no advertising, and there are no third‑party tracking or
              marketing SDKs inside the app. You can read the entire book without creating an account.
            </p>
          </Section>

          <Section title="Anonymous reader identity">
            <p>
              When you begin reading, the app creates an <strong>anonymous reader id</strong> — a random
              token stored on your device and on our server. It is not linked to your name, email,
              phone number, or any real‑world identity. Its only job is to remember your reading
              progress and let you move between devices.
            </p>
            <p>
              To continue on another device you can generate a human‑readable <strong>Trail Code</strong>
              (for example <em>RIDGE‑CEDAR‑4182</em>). Anyone who enters that code can restore that
              reading session, so share it only with yourself. You can regenerate or revoke a Trail Code
              at any time from the “Your Trail” menu.
            </p>
          </Section>

          <Section title="Your private field journal">
            <p>
              If you write <strong>field journal</strong> entries, they are stored against your anonymous
              reader id so you can read them again and export them as a PDF. They are private to your
              reader session. We do not read them, sell them, or share them, and they are never used for
              advertising.
            </p>
            <p>
              You can delete all of your journal entries, or erase your entire anonymous reader record
              (journal included), at any time from within the app.
            </p>
          </Section>

          <Section title="Information stored on your device">
            <p>
              The app keeps a few preferences in local storage on your device — for example your reading
              position/bookmark, music volume, whether page‑turn haptics are on, and the portrait‑lock
              setting. In the installed/offline app, the book, audio and search index are bundled inside
              the app and read locally; no network connection is required to read.
            </p>
          </Section>

          <Section title="What we do not collect">
            <ul className="list-disc space-y-1 pl-5">
              <li>No names, email addresses, or phone numbers.</li>
              <li>No advertising identifiers and no third‑party ad or analytics SDKs in the app.</li>
              <li>No precise location, contacts, photos, or microphone access.</li>
            </ul>
          </Section>

          <Section title="How information is used">
            <p>
              Solely to provide the reading experience: to save and restore your progress, store your
              journal, and sync across your devices when you choose to. We do not sell personal
              information.
            </p>
          </Section>

          <Section title="Data retention & deletion">
            <p>
              Your anonymous reader record and journal remain until you delete them in the app. Because
              the reader id is anonymous, deleting it removes the associated data from our server.
            </p>
          </Section>

          <Section title="Children">
            <p>
              The book is written for an adult audience and is not directed at children under 13. We do
              not knowingly collect personal information from children.
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>
              If this policy changes, we will update the “Effective” date above and post the revised
              policy at this page.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions about privacy? Contact Casey Muze at{" "}
              <a href={`mailto:${CONTACT}`} className="text-gold-deep underline" data-testid="privacy-contact">
                {CONTACT}
              </a>
              .
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
